/**
 * preview-flag — Govern surface gating hook.
 *
 * `useGovernPreviewActive()` returns true when EITHER the production env flag
 * `VITE_ENABLE_GOVERN_SURFACE` is `'true'`, OR the URL carries the local-dev
 * override `?preview=govern[.<subsurface>]`. Every new Govern register route
 * shell gates its placeholder render on this hook; the existing `/govern/*`
 * zone is unaffected.
 *
 * `?preview=govern[.<subsurface>]` is a LOCAL-DEV override only (e.g.
 * `?preview=govern`, `?preview=govern.policy`, `?preview=govern.walk`). It
 * lets the campaign exercise the scaffolded surface in dev while the env flag
 * stays `false` in production until the Session 9 flag flip. Any `preview`
 * value beginning with `govern` activates the buffer.
 *
 * Constitutional basis:
 *   - I-AX-04 (Stability): read-only gate; introduces no state-mutation surface.
 *   - V-AX-02 (Structure): a single new FE flag (`VITE_ENABLE_GOVERN_SURFACE`);
 *     a second `VITE_ENABLE_GOVERN_*` flag is a campaign HALT condition.
 *
 * Spec: SN-FE-GOV-001 §8 (flag posture + preview buffer) · Session 4.
 */

import { useSearchParams } from 'react-router-dom';

/**
 * True when the Govern register surface should render its scaffolded
 * placeholder: the production env flag is enabled, or a `?preview=govern…`
 * local-dev override is present in the URL.
 */
export function useGovernPreviewActive(): boolean {
  const [searchParams] = useSearchParams();
  const envFlagOn = import.meta.env.VITE_ENABLE_GOVERN_SURFACE === 'true';
  const previewParamOn =
    searchParams.get('preview')?.startsWith('govern') ?? false;
  return envFlagOn || previewParamOn;
}
